import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { mapDiagnosis } from '@/features/patient-record/mappers/diagnosis';
import { patientQueries } from '@/features/patient-record/queries';
import type {
  DiagnosisStatus,
  DiagnosisViewModel,
  DiagnosisVocabulary,
} from '@/features/patient-record/types';

/**
 * Resource-level mutations for vocabulary-term diagnoses. Add lands via the shared
 * `VocabularyTermPicker` (PTV2-1042). The hook exposes `add`, `update`
 * (status / age / notes), and `remove`.
 */

export interface AddDiagnosisVariables {
  readonly termId: string;
  readonly vocabulary: DiagnosisVocabulary;
  readonly title: string;
  readonly status: DiagnosisStatus;
  readonly ageAtDiagnosis: string | null;
  readonly notes: string | null;
}

export interface UpdateDiagnosisVariables {
  readonly id: string;
  readonly termId: string;
  readonly vocabulary: DiagnosisVocabulary;
  readonly title: string;
  readonly status: DiagnosisStatus;
  readonly ageAtDiagnosis: string | null;
  readonly notes: string | null;
}

interface MutationContext {
  readonly previous: DiagnosisViewModel[] | undefined;
}

type AddResult = UseMutationResult<
  DiagnosisViewModel,
  Error,
  AddDiagnosisVariables
>;
type UpdateResult = UseMutationResult<
  DiagnosisViewModel,
  Error,
  UpdateDiagnosisVariables,
  MutationContext
>;
type RemoveResult = UseMutationResult<undefined, Error, string, MutationContext>;

export function useDiagnosisMutations(guid: string): {
  readonly add: AddResult;
  readonly update: UpdateResult;
  readonly remove: RemoveResult;
} {
  const queryClient = useQueryClient();
  const { queryKey } = patientQueries.diagnoses(guid);

  const snapshot = (): MutationContext => ({
    previous: queryClient.getQueryData(queryKey),
  });

  const rollback = (ctx: MutationContext | undefined): void => {
    if (ctx === undefined) return;
    queryClient.setQueryData(queryKey, ctx.previous);
  };

  const settle = (): void => {
    queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
  };

  const add = useMutation<DiagnosisViewModel, Error, AddDiagnosisVariables>({
    meta: { successMessage: 'success.toast.title.diagnosis_created' },
    mutationFn: async (vars) => {
      const dto = await apiClient.addDiagnosis(guid, {
        term_id: vars.termId,
        vocabulary: vars.vocabulary,
        title: vars.title,
        status: vars.status,
        age_at_diagnosis: vars.ageAtDiagnosis,
        notes: vars.notes,
      });
      return mapDiagnosis(dto);
    },
    onSettled: settle,
  });

  const update = useMutation<
    DiagnosisViewModel,
    Error,
    UpdateDiagnosisVariables,
    MutationContext
  >({
    meta: { successMessage: 'success.toast.title.diagnosis_updated' },
    mutationFn: async (vars) => {
      const dto = await apiClient.updateDiagnosis(guid, vars.id, {
        term_id: vars.termId,
        vocabulary: vars.vocabulary,
        title: vars.title,
        status: vars.status,
        age_at_diagnosis: vars.ageAtDiagnosis,
        notes: vars.notes,
      });
      return mapDiagnosis(dto);
    },
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      queryClient.setQueryData<DiagnosisViewModel[]>(
        queryKey,
        (ctx.previous ?? []).map((d) =>
          d.id === vars.id
            ? {
                ...d,
                status: vars.status,
                ageAtDiagnosis: vars.ageAtDiagnosis,
                notes: vars.notes,
              }
            : d,
        ),
      );
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  const remove = useMutation<undefined, Error, string, MutationContext>({
    meta: { successMessage: 'success.toast.title.diagnosis_deleted' },
    mutationFn: (id) => apiClient.deleteDiagnosis(guid, id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const ctx = snapshot();
      queryClient.setQueryData<DiagnosisViewModel[]>(
        queryKey,
        (ctx.previous ?? []).filter((d) => d.id !== id),
      );
      return ctx;
    },
    onError: (_e, _v, ctx) => {
      rollback(ctx);
    },
    onSettled: settle,
  });

  return { add, update, remove };
}
